import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType } from 'vopay';
import useFetchEFTFundTransaction from '@/hooks/useApi/useFetchEFTFundTransaction';
import EftFundingTransactionInfo from './EftFundingTransactionInfo';
import LinkedTransactionInfo from './LinkedTransactionInfo';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type EftFundingTransactionProps = {
  TransactionID: string;
} & Omit<ComponentProps<typeof EftFundingTransactionInfo>, 'transaction' | 'subTransactionId' | 'subTransactionType'>;

export default function EftFundingTransactionContainer({
  TransactionID,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  ...props
}: EftFundingTransactionProps) {
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { userSettings } = useUserSettingsProvider();

  const { eftFundTransaction, fetchEftFundTransaction, resetEftFundTransaction } = useFetchEFTFundTransaction({
    bearer: jwt,
  });

  useEffect(() => {
    blockingLoader.show();
    resetEftFundTransaction();

    fetchEftFundTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) =>
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred')),
      )
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = eftFundTransaction;

  const subTransactions = eftFundTransaction?.SubTransactions
    ? Object.values(eftFundTransaction.SubTransactions)
    : undefined;

  const withdrawalSubTransaction = subTransactions?.find(
    (subTransaction) => subTransaction.TransactionType === TransactionType.EFT_WITHDRAWAL,
  );

  return (
    <div>
      {transaction && (
        <EftFundingTransactionInfo
          transaction={transaction}
          Notes={Notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          subTransactionId={withdrawalSubTransaction?.TransactionID}
          subTransactionType={withdrawalSubTransaction ? 'EFT Withdrawal' : undefined}
          {...props}
        />
      )}

      {subTransactions && subTransactions.length > 0 && (
        <LinkedTransactionInfo LinkedTransactions={subTransactions} Currency={transaction?.Currency || 'CAD'} />
      )}
    </div>
  );
}
